import React, { useMemo, useRef, useEffect, useState } from 'react';
import { MapPin, Ruler, Navigation, Loader2 } from 'lucide-react';

// Lazy loaded map component to avoid React StrictMode double mount issues
const FarmMap = ({ farms = [], height = '400px', onFarmClick }) => {
  const containerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [isReady, setIsReady] = useState(false);

  // Default center on France
  const defaultCenter = [46.603354, 1.888334];
  const defaultZoom = 6;

  // Filter farms with valid coordinates
  const validFarms = useMemo(() => 
    farms.filter(farm => farm.latitude && farm.longitude),
    [farms]
  );

  // Calculate center based on farms
  const center = useMemo(() => {
    if (validFarms.length === 0) return defaultCenter;
    const avgLat = validFarms.reduce((sum, f) => sum + f.latitude, 0) / validFarms.length;
    const avgLng = validFarms.reduce((sum, f) => sum + f.longitude, 0) / validFarms.length;
    return [avgLat, avgLng];
  }, [validFarms]);

  // Initialize map using vanilla Leaflet
  useEffect(() => {
    let map = null;
    let mounted = true;

    const initMap = async () => {
      if (!containerRef.current || mapInstanceRef.current) return;

      try {
        const L = await import('leaflet');
        await import('leaflet/dist/leaflet.css');

        if (!mounted || !containerRef.current) return;

        // Fix default icons
        delete L.default.Icon.Default.prototype._getIconUrl;
        L.default.Icon.Default.mergeOptions({
          iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
          iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
        });

        // Create map
        map = L.default.map(containerRef.current).setView(center, defaultZoom);
        mapInstanceRef.current = map;

        // Add tile layer
        L.default.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        }).addTo(map);

        setIsReady(true);
      } catch (error) {
        console.error('Failed to initialize map:', error);
      }
    };

    initMap();

    return () => {
      mounted = false;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Update markers when farms change
  useEffect(() => {
    const updateMarkers = async () => {
      if (!mapInstanceRef.current || !isReady) return;

      const L = await import('leaflet');
      const map = mapInstanceRef.current;

      // Clear existing markers
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];

      // Create custom icon
      const farmIcon = L.default.divIcon({
        className: 'custom-farm-marker',
        html: `<div style="
          background-color: #0F5338;
          width: 32px;
          height: 32px;
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          display: flex;
          align-items: center;
          justify-content: center;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        ">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="transform: rotate(45deg);">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
            <circle cx="12" cy="10" r="3"></circle>
          </svg>
        </div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32],
      });

      // Add markers for each farm
      validFarms.forEach(farm => {
        const marker = L.default.marker([farm.latitude, farm.longitude], { icon: farmIcon })
          .addTo(map)
          .bindPopup(`
            <div style="min-width: 200px; padding: 4px;">
              <h3 style="font-weight: 600; font-size: 14px; margin-bottom: 8px; color: #1e293b;">
                ${farm.name}
              </h3>
              ${farm.description ? `<p style="font-size: 12px; color: #64748b; margin-bottom: 8px;">${farm.description}</p>` : ''}
              <div style="font-size: 11px; color: #64748b;">
                ${farm.address ? `<div style="display: flex; align-items: center; gap: 4px; margin-bottom: 4px;">📍 ${farm.address}</div>` : ''}
                ${farm.total_area ? `<div style="display: flex; align-items: center; gap: 4px; margin-bottom: 4px;">📐 ${farm.total_area} ha</div>` : ''}
                <div style="display: flex; align-items: center; gap: 4px;">🧭 ${farm.latitude.toFixed(4)}, ${farm.longitude.toFixed(4)}</div>
              </div>
            </div>
          `);

        if (onFarmClick) {
          marker.on('click', () => onFarmClick(farm));
        }

        markersRef.current.push(marker);
      });

      // Fit bounds if we have farms
      if (validFarms.length > 0) {
        const bounds = L.default.latLngBounds(validFarms.map(f => [f.latitude, f.longitude]));
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 10 });
      }
    };

    updateMarkers();
  }, [validFarms, isReady, onFarmClick]);

  return (
    <div 
      className="relative rounded-xl overflow-hidden border bg-card shadow-sm"
      style={{ height }}
      data-testid="farm-map"
    >
      <div 
        ref={containerRef} 
        style={{ height: '100%', width: '100%' }}
        className="z-0"
      />
      
      {!isReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-card">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Chargement de la carte...</p>
          </div>
        </div>
      )}
      
      {/* Legend */}
      {isReady && (
        <div className="absolute bottom-4 left-4 z-[1000] bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg border">
          <div className="flex items-center gap-2 text-xs">
            <div className="w-3 h-3 rounded-full bg-primary" />
            <span className="text-slate-600 dark:text-slate-300">
              {validFarms.length} exploitation{validFarms.length > 1 ? 's' : ''}
            </span>
          </div>
        </div>
      )}
      
      {/* No farms message */}
      {isReady && validFarms.length === 0 && (
        <div className="absolute inset-0 z-[1000] flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 rounded-lg px-4 py-3 shadow-lg text-center">
            <MapPin className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              Aucune exploitation avec coordonnées GPS
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FarmMap;
